import React, { useState, useEffect, createContext, useContext } from 'react';

// ── Auth Context ──────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('adtsync_token'));
  const [username, setUsername] = useState(() => localStorage.getItem('adtsync_user'));

  const login = (tok, user) => {
    setToken(tok);
    setUsername(user);
    localStorage.setItem('adtsync_token', tok);
    localStorage.setItem('adtsync_user', user);
  };

  const logout = () => {
    setToken(null);
    setUsername(null);
    localStorage.removeItem('adtsync_token');
    localStorage.removeItem('adtsync_user');
  };

  return (
    <AuthContext.Provider value={{ token, username, login, logout, isAuthed: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── API Helper ────────────────────────────────────────────────────────────────

export function useApi() {
  const { token, logout } = useAuth();

  const call = async (method, path, body) => {
    const res = await fetch(`/api${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (res.status === 401) { logout(); return null; }
    if (!res.ok) throw new Error(await res.text());
    if (res.status === 204) return null;
    return res.json();
  };

  return {
    get: (path) => call('GET', path),
    post: (path, body) => call('POST', path, body),
    put: (path, body) => call('PUT', path, body),
    patch: (path, body) => call('PATCH', path, body),
    del: (path) => call('DELETE', path),
  };
}

// ── Login Page ────────────────────────────────────────────────────────────────

function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      if (!res.ok) { setError('Invalid credentials.'); return; }
      const data = await res.json();
      login(data.token, data.username);
    } catch {
      setError('Login failed. Server unreachable.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginCard}>
        <img src="https://advdigitech.com/wp-content/uploads/2021/04/ADT-logo-black.png"
             alt="ADT Logo" style={styles.loginLogo} />
        <h2 style={styles.loginTitle}>ADTSync Admin</h2>
        <form onSubmit={handleSubmit}>
          <input style={styles.input} placeholder="Username" value={username}
                 onChange={e => setUsername(e.target.value)} required />
          <input style={styles.input} type="password" placeholder="Password"
                 value={password} onChange={e => setPassword(e.target.value)} required />
          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Shell / Nav ───────────────────────────────────────────────────────────────

function Shell({ children, page, setPage }) {
  const { username, logout } = useAuth();
  const nav = [
    { id: 'clients', label: '👥 Clients' },
    { id: 'settings', label: '⚙️ Settings' },
  ];

  return (
    <div style={styles.shell}>
      <div style={styles.sidebar}>
        <img src="https://advdigitech.com/wp-content/uploads/2021/04/ADT-logo-black.png"
             alt="ADT" style={styles.sidebarLogo} />
        <div style={styles.sidebarTitle}>ADTSync</div>
        {nav.map(n => (
          <div key={n.id}
               style={{ ...styles.navItem, ...(page === n.id ? styles.navActive : {}) }}
               onClick={() => setPage(n.id)}>
            {n.label}
          </div>
        ))}
        <div style={styles.sidebarFooter}>
          <div style={styles.navUser}>👤 {username}</div>
          <div style={{ ...styles.navItem, color: '#e74c3c' }} onClick={logout}>🚪 Sign Out</div>
        </div>
      </div>
      <div style={styles.main}>{children}</div>
    </div>
  );
}

// ── Clients Page ──────────────────────────────────────────────────────────────

function ClientsPage() {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newClientName, setNewClientName] = useState('');
  const [newKey, setNewKey] = useState(null); // {apiKey, aesKey, clientId, mappingId}
  const [loading, setLoading] = useState(true);
  const [showMappingForm, setShowMappingForm] = useState(false);

  const load = async () => {
    setLoading(true);
    const data = await api.get('/clients');
    setClients(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createClient = async () => {
    if (!newClientName.trim()) return;
    const res = await api.post('/clients', { name: newClientName });
    setNewKey({ type: 'client', apiKey: res.apiKey, clientId: res.id });
    setNewClientName('');
    load();
  };

  const toggleClient = async (c) => {
    await api.patch(`/clients/${c.id}/enabled`, !c.enabled);
    load();
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    await api.del(`/clients/${c.id}`);
    setSelected(null);
    load();
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Clients</h1>

      {/* New Key Modal */}
      {newKey && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h3>⚠️ Save These Credentials Now</h3>
            <p style={{ color: '#e74c3c' }}>These values are shown <strong>once only</strong> and cannot be recovered.</p>
            {newKey.apiKey && <KeyDisplay label="API Key" value={newKey.apiKey} />}
            {newKey.aesKey && <KeyDisplay label="AES-256 Key (Base64)" value={newKey.aesKey} />}
            {newKey.clientId && <KeyDisplay label="Client ID" value={newKey.clientId} />}
            {newKey.mappingId && <KeyDisplay label="Mapping ID" value={newKey.mappingId} />}
            <button style={styles.btn} onClick={() => setNewKey(null)}>I have saved these — Close</button>
          </div>
        </div>
      )}

      <div style={styles.twoCol}>
        {/* Left: client list */}
        <div style={styles.listPanel}>
          <div style={styles.addRow}>
            <input style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                   placeholder="New client name" value={newClientName}
                   onChange={e => setNewClientName(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && createClient()} />
            <button style={{ ...styles.btn, marginLeft: 8, padding: '8px 14px' }}
                    onClick={createClient}>Add</button>
          </div>
          {loading ? <Spinner /> : clients.map(c => (
            <div key={c.id}
                 style={{ ...styles.listItem, ...(selected?.id === c.id ? styles.listItemActive : {}) }}
                 onClick={() => setSelected(c)}>
              <div>
                <strong>{c.name}</strong>
                <div style={styles.small}>{c.mappingCount} mapping(s)</div>
              </div>
              <span style={{ color: c.enabled ? '#27ae60' : '#e74c3c' }}>
                {c.enabled ? '●' : '○'}
              </span>
            </div>
          ))}
        </div>

        {/* Right: client detail */}
        {selected && (
          <ClientDetail
            client={selected}
            api={api}
            onRefresh={() => { load(); setSelected(s => ({ ...s })); }}
            onToggle={() => toggleClient(selected)}
            onDelete={() => deleteClient(selected)}
            onNewKey={setNewKey}
          />
        )}
      </div>
    </div>
  );
}

function ClientDetail({ client, api, onRefresh, onToggle, onDelete, onNewKey }) {
  const [mappings, setMappings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editMapping, setEditMapping] = useState(null);

  const loadMappings = async () => {
    const data = await api.get(`/clients/${client.id}`);
    setMappings(data?.mappings || []);
  };

  useEffect(() => { loadMappings(); }, [client.id]);

  const deleteMapping = async (m) => {
    if (!window.confirm(`Delete mapping "${m.label}"?`)) return;
    await api.del(`/clients/${client.id}/mappings/${m.id}`);
    loadMappings();
  };

  return (
    <div style={styles.detailPanel}>
      <div style={styles.detailHeader}>
        <h2 style={{ margin: 0 }}>{client.name}</h2>
        <div>
          <button style={{ ...styles.btnSm, marginRight: 6 }} onClick={onToggle}>
            {client.enabled ? 'Disable' : 'Enable'}
          </button>
          <button style={{ ...styles.btnSm, background: '#e74c3c' }} onClick={onDelete}>Delete</button>
        </div>
      </div>
      <div style={styles.small}>ID: {client.id} · Created: {new Date(client.createdUtc).toLocaleDateString()}</div>

      <h3 style={{ marginTop: 20 }}>Watch Folder Mappings</h3>
      {mappings.map(m => (
        <div key={m.id} style={styles.mappingCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <strong>{m.label}</strong>
            <div>
              <button style={styles.btnSm} onClick={() => { setEditMapping(m); setShowForm(true); }}>Edit</button>
              <button style={{ ...styles.btnSm, background: '#e74c3c', marginLeft: 4 }}
                      onClick={() => deleteMapping(m)}>Remove</button>
            </div>
          </div>
          <div style={styles.small}>
            Server path: {m.serverFolderPath} ·
            Reconcile: {m.reconciliationIntervalMinutes}min ·
            Chunks: {m.chunkSizeMb}MB ·
            Workers: {m.parallelWorkers} ·
            Batch: {m.batchingEnabled ? `✓ (≤${m.batchSmallFileThresholdMb}MB)` : '✗'} ·
            UNC: {m.uncExportEnabled ? `✓ ${m.uncPath}` : '✗'}
          </div>
        </div>
      ))}

      <button style={{ ...styles.btn, marginTop: 12 }} onClick={() => { setEditMapping(null); setShowForm(true); }}>
        + Add Mapping
      </button>

      {showForm && (
        <MappingForm
          initial={editMapping}
          onSave={async (data) => {
            let res;
            if (editMapping) {
              await api.put(`/clients/${client.id}/mappings/${editMapping.id}`, data);
            } else {
              res = await api.post(`/clients/${client.id}/mappings`, data);
              if (res?.aesKeyBase64) {
                onNewKey({ type: 'mapping', aesKey: res.aesKeyBase64, mappingId: res.id, clientId: client.id });
              }
            }
            setShowForm(false);
            setEditMapping(null);
            loadMappings();
          }}
          onCancel={() => { setShowForm(false); setEditMapping(null); }}
        />
      )}
    </div>
  );
}

function MappingForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({
    label: initial?.label || '',
    serverFolderPath: initial?.serverFolderPath || '',
    reconciliationIntervalMinutes: initial?.reconciliationIntervalMinutes || 5,
    chunkSizeMb: initial?.chunkSizeMb || 5,
    parallelWorkers: initial?.parallelWorkers || 4,
    batchingEnabled: initial?.batchingEnabled || false,
    batchSmallFileThresholdMb: initial?.batchSmallFileThresholdMb || 2,
    batchMaxSizeMb: initial?.batchMaxSizeMb || 20,
    batchMaxWaitSeconds: initial?.batchMaxWaitSeconds || 5,
    uncExportEnabled: initial?.uncExportEnabled || false,
    uncPath: initial?.uncPath || '',
    uncUsername: '',
    uncPassword: '',
  });

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  return (
    <div style={styles.formCard}>
      <h3>{initial ? 'Edit Mapping' : 'New Mapping'}</h3>

      <Section title="General">
        <Field label="Label"><input style={styles.input} value={f.label} onChange={e => set('label', e.target.value)} /></Field>
        <Field label="Server Folder Path"><input style={styles.input} value={f.serverFolderPath} onChange={e => set('serverFolderPath', e.target.value)} placeholder="/data/clients/clientname/" /></Field>
      </Section>

      <Section title="Sync Settings">
        <Field label="Reconcile Interval (min)"><NumInput value={f.reconciliationIntervalMinutes} min={1} max={1440} onChange={v => set('reconciliationIntervalMinutes', v)} /></Field>
        <Field label="Chunk Size (MB)"><NumInput value={f.chunkSizeMb} min={1} max={100} onChange={v => set('chunkSizeMb', v)} /></Field>
        <Field label="Parallel Workers"><NumInput value={f.parallelWorkers} min={1} max={16} onChange={v => set('parallelWorkers', v)} /></Field>
      </Section>

      <Section title="Batching">
        <Field label="Enable Batching">
          <input type="checkbox" checked={f.batchingEnabled} onChange={e => set('batchingEnabled', e.target.checked)} />
        </Field>
        <Field label="Small File Threshold (MB)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchSmallFileThresholdMb} min={1} max={50} onChange={v => set('batchSmallFileThresholdMb', v)} disabled={!f.batchingEnabled} />
        </Field>
        <Field label="Batch Max Size (MB)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchMaxSizeMb} min={1} max={200} onChange={v => set('batchMaxSizeMb', v)} disabled={!f.batchingEnabled} />
        </Field>
        <Field label="Batch Max Wait (sec)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchMaxWaitSeconds} min={1} max={300} onChange={v => set('batchMaxWaitSeconds', v)} disabled={!f.batchingEnabled} />
        </Field>
      </Section>

      <Section title="UNC Export">
        <Field label="Enable UNC Export">
          <input type="checkbox" checked={f.uncExportEnabled} onChange={e => set('uncExportEnabled', e.target.checked)} />
        </Field>
        <Field label="UNC Path" dim={!f.uncExportEnabled}><input style={styles.input} value={f.uncPath} onChange={e => set('uncPath', e.target.value)} placeholder="\\server\share\folder" disabled={!f.uncExportEnabled} /></Field>
        <Field label="UNC Username" dim={!f.uncExportEnabled}><input style={styles.input} value={f.uncUsername} onChange={e => set('uncUsername', e.target.value)} disabled={!f.uncExportEnabled} /></Field>
        <Field label="UNC Password" dim={!f.uncExportEnabled}><input style={styles.input} type="password" value={f.uncPassword} onChange={e => set('uncPassword', e.target.value)} disabled={!f.uncExportEnabled} /></Field>
      </Section>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={styles.btn} onClick={() => onSave(f)}>Save Mapping</button>
        <button style={{ ...styles.btn, background: '#7f8c8d' }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Settings Page ─────────────────────────────────────────────────────────────

function SettingsPage() {
  const api = useApi();
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get('/config').then(setCfg); }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    await api.put('/config', cfg);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (!cfg) return <Spinner />;

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Global Settings</h1>

      <Section title="Sync Defaults">
        <Field label="Default Reconcile Interval (min)"><NumInput value={cfg.defaultReconciliationIntervalMinutes} min={1} max={1440} onChange={v => set('defaultReconciliationIntervalMinutes', v)} /></Field>
        <Field label="Default Chunk Size (MB)"><NumInput value={cfg.defaultChunkSizeMb} min={1} max={100} onChange={v => set('defaultChunkSizeMb', v)} /></Field>
        <Field label="Default Parallel Workers"><NumInput value={cfg.defaultParallelWorkers} min={1} max={16} onChange={v => set('defaultParallelWorkers', v)} /></Field>
      </Section>

      <Section title="Log Retention">
        <Field label="Retain Logs (days)"><NumInput value={cfg.logRetentionDays} min={7} max={3650} onChange={v => set('logRetentionDays', v)} /></Field>
      </Section>

      <Section title="Active Directory / LDAP">
        <Field label="Enable AD Auth"><input type="checkbox" checked={cfg.ldapEnabled} onChange={e => set('ldapEnabled', e.target.checked)} /></Field>
        <Field label="AD Server Host"><input style={styles.input} value={cfg.ldapHost || ''} onChange={e => set('ldapHost', e.target.value)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="LDAP Port"><NumInput value={cfg.ldapPort || 389} min={1} max={65535} onChange={v => set('ldapPort', v)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="Use SSL"><input type="checkbox" checked={cfg.ldapUseSsl || false} onChange={e => set('ldapUseSsl', e.target.checked)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="Base DN"><input style={styles.input} value={cfg.ldapBaseDn || ''} onChange={e => set('ldapBaseDn', e.target.value)} placeholder="DC=corp,DC=example,DC=com" disabled={!cfg.ldapEnabled} /></Field>
      </Section>

      <button style={styles.btn} onClick={save}>Save Settings</button>
      {saved && <span style={{ marginLeft: 12, color: '#27ae60' }}>✓ Saved</span>}
    </div>
  );
}

// ── Shared Components ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={styles.sectionHeader}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children, dim }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
      <label style={{ ...styles.fieldLabel, opacity: dim ? 0.4 : 1 }}>{label}</label>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, min, max, onChange, disabled }) {
  return (
    <input type="number" style={{ ...styles.input, width: 100, marginBottom: 0 }}
           value={value} min={min} max={max} disabled={disabled}
           onChange={e => onChange(parseInt(e.target.value) || min)} />
  );
}

function KeyDisplay({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={styles.small}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly style={{ ...styles.input, fontFamily: 'monospace', fontSize: 12, marginBottom: 0, flex: 1 }} value={value} />
        <button style={styles.btnSm} onClick={() => { navigator.clipboard.writeText(value); setCopied(true); }}>
          {copied ? '✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function Spinner() {
  return <div style={{ padding: 20, color: '#7f8c8d' }}>Loading...</div>;
}

// ── Root App ──────────────────────────────────────────────────────────────────

function App() {
  const [page, setPage] = useState('clients');
  const { isAuthed } = useAuth();

  if (!isAuthed) return <LoginPage />;

  return (
    <Shell page={page} setPage={setPage}>
      {page === 'clients' && <ClientsPage />}
      {page === 'settings' && <SettingsPage />}
    </Shell>
  );
}

export default function Root() {
  return <AuthProvider><App /></AuthProvider>;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  loginWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f0f2f5' },
  loginCard: { background: '#fff', borderRadius: 12, padding: 40, width: 360, boxShadow: '0 4px 24px rgba(0,0,0,0.1)' },
  loginLogo: { width: 180, marginBottom: 16, display: 'block' },
  loginTitle: { marginBottom: 20, color: '#2c3e50', fontSize: 22 },
  shell: { display: 'flex', minHeight: '100vh', fontFamily: 'system-ui, sans-serif', color: '#2c3e50' },
  sidebar: { width: 220, background: '#2c3e50', display: 'flex', flexDirection: 'column', padding: '20px 0' },
  sidebarLogo: { width: 140, margin: '0 auto 8px', display: 'block', filter: 'invert(1)' },
  sidebarTitle: { color: '#ecf0f1', textAlign: 'center', fontWeight: 700, fontSize: 14, marginBottom: 20, letterSpacing: 1 },
  navItem: { padding: '10px 20px', color: '#bdc3c7', cursor: 'pointer', borderRadius: 4, margin: '2px 8px', transition: 'background 0.15s' },
  navActive: { background: '#3498db', color: '#fff' },
  navUser: { padding: '8px 20px', color: '#7f8c8d', fontSize: 13 },
  sidebarFooter: { marginTop: 'auto' },
  main: { flex: 1, background: '#f7f9fc', overflowY: 'auto' },
  page: { padding: 32, maxWidth: 1100 },
  pageTitle: { fontSize: 26, fontWeight: 700, marginBottom: 24, color: '#2c3e50' },
  twoCol: { display: 'flex', gap: 24 },
  listPanel: { width: 280, flexShrink: 0 },
  detailPanel: { flex: 1, background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  listItem: { padding: '12px 14px', background: '#fff', borderRadius: 8, marginBottom: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  listItemActive: { background: '#ebf5fb', borderLeft: '3px solid #3498db' },
  addRow: { display: 'flex', marginBottom: 10, alignItems: 'center' },
  mappingCard: { background: '#f7f9fc', borderRadius: 8, padding: 12, marginBottom: 8 },
  formCard: { background: '#f7f9fc', borderRadius: 10, padding: 20, marginTop: 16 },
  sectionHeader: { fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: '#7f8c8d', marginBottom: 10, borderBottom: '1px solid #e0e6ed', paddingBottom: 4 },
  fieldLabel: { width: 220, fontSize: 13, color: '#555', flexShrink: 0 },
  input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d5dbe3', fontSize: 13, marginBottom: 6, boxSizing: 'border-box' },
  btn: { background: '#3498db', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', cursor: 'pointer', fontSize: 14 },
  btnSm: { background: '#3498db', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 12 },
  small: { fontSize: 12, color: '#7f8c8d', marginTop: 2 },
  error: { color: '#e74c3c', fontSize: 13, marginBottom: 10 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { background: '#fff', borderRadius: 12, padding: 32, width: 480, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' },
};
